import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CouponService, CouponDto, CouponUsageResponse } from './coupon.service';
import { NotificationService } from '../../core/notification.service';

@Component({
  standalone: false,
  selector: 'app-coupons-list',
  templateUrl: './coupons-list.component.html',
  styleUrls: ['./coupons-list.component.css']
})
export class CouponsListComponent implements OnInit {
  loading = false;
  saving = false;
  page = 1;
  pageSize = 20;
  total = 0;

  // Filters
  search = '';
  statusFilter = '';
  typeFilter = '';

  items: CouponDto[] = [];

  // Modal state
  showFormModal = false;
  editingItem: CouponDto | null = null;
  form!: FormGroup;

  // Usage modal
  showUsageModal = false;
  usageData: CouponUsageResponse | null = null;
  loadingUsage = false;

  constructor(
    private svc: CouponService,
    private notify: NotificationService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.load();
  }

  buildForm(coupon?: CouponDto) {
    this.form = this.fb.group({
      code: [coupon?.code || '', [Validators.required, Validators.maxLength(20), Validators.pattern(/^[A-Z0-9-]+$/)]],
      description: [coupon?.description || '', [Validators.maxLength(200)]],
      discountType: [coupon?.discountType || 'percentage', Validators.required],
      discountValue: [coupon?.discountValue ?? '', [Validators.required, Validators.min(0.01)]],
      maxUses: [coupon?.maxUses ?? ''],
      expiresAt: [coupon?.expiresAt ? coupon.expiresAt.slice(0, 10) : '', Validators.required],
      minPurchaseAmount: [coupon?.minPurchaseAmount ?? ''],
      isActive: [coupon?.isActive ?? true]
    });
  }

  load(): void {
    this.loading = true;
    const params: any = { page: this.page, pageSize: this.pageSize };
    if (this.search) params.search = this.search;
    if (this.statusFilter) params.status = this.statusFilter;
    if (this.typeFilter) params.discountType = this.typeFilter;

    this.svc.list(params).subscribe({
      next: (res: any) => {
        console.log('[Coupons] resposta da API:', res);
        this.items = res?.coupons ?? res?.data ?? (Array.isArray(res) ? res : []);
        console.log('[Coupons] items carregados:', this.items);
        this.total = res?.meta?.total ?? res?.total ?? this.items.length;
        this.loading = false;
      },
      error: (err) => {
        console.error('[Coupons] erro ao listar', err);
        this.notify.showError('Erro ao carregar cupons');
        this.loading = false;
      }
    });
  }

  onPageChange(p: number) {
    this.page = p;
    this.load();
  }

  resetFilters(): void {
    this.search = '';
    this.statusFilter = '';
    this.typeFilter = '';
    this.page = 1;
    this.load();
  }

  openCreate(): void {
    this.editingItem = null;
    this.buildForm();
    this.showFormModal = true;
  }

  openEdit(item: CouponDto): void {
    this.editingItem = item;
    this.buildForm(item);
    this.showFormModal = true;
  }

  closeFormModal(): void {
    this.showFormModal = false;
    this.editingItem = null;
  }

  save(): void {
    if (this.form.invalid || this.saving) return;
    this.saving = true;

    const raw = this.form.value;
    const payload: any = {
      code: (raw.code || '').toUpperCase().trim(),
      description: raw.description || undefined,
      discountType: raw.discountType,
      discountValue: Number(raw.discountValue),
      expiresAt: raw.expiresAt ? new Date(`${raw.expiresAt}T00:00:00`).toISOString() : undefined,
      maxUses: raw.maxUses ? Number(raw.maxUses) : undefined,
      minPurchaseAmount: raw.minPurchaseAmount ? Number(raw.minPurchaseAmount) : undefined
    };

    if (this.editingItem) {
      payload.isActive = raw.isActive;
      this.svc.update(this.editingItem.id, payload).subscribe({
        next: (updated) => {
          this.saving = false;
          const idx = this.items.findIndex(c => c.id === this.editingItem!.id);
          if (idx >= 0) this.items[idx] = updated;
          this.closeFormModal();
          this.notify.showSuccess('Cupom atualizado com sucesso');
        },
        error: (err) => {
          this.saving = false;
          console.error('[Coupons] erro ao atualizar', err);
          this.notify.showError('Erro ao atualizar cupom');
        }
      });
    } else {
      this.svc.create(payload).subscribe({
        next: () => {
          this.saving = false;
          this.closeFormModal();
          this.notify.showSuccess('Cupom criado com sucesso');
          this.load();
        },
        error: (err) => {
          this.saving = false;
          console.error('[Coupons] erro ao criar', err);
          this.notify.showError('Erro ao criar cupom');
        }
      });
    }
  }

  remove(item: CouponDto): void {
    if (!confirm(`Deletar o cupom "${item.code}"?`)) return;
    this.svc.delete(item.id).subscribe({
      next: () => {
        this.items = this.items.filter(c => c.id !== item.id);
        this.notify.showSuccess('Cupom removido');
      },
      error: (err) => {
        console.error('[Coupons] erro ao remover', err);
        this.notify.showError('Erro ao remover cupom');
      }
    });
  }

  viewUsage(item: CouponDto): void {
    this.usageData = null;
    this.loadingUsage = true;
    this.showUsageModal = true;
    this.svc.getUsage(item.id).subscribe({
      next: (res) => {
        this.usageData = res;
        this.loadingUsage = false;
      },
      error: (err) => {
        console.error('[Coupons] erro ao carregar uso', err);
        this.loadingUsage = false;
        this.notify.showError('Erro ao carregar uso do cupom');
      }
    });
  }

  closeUsageModal(): void {
    this.showUsageModal = false;
    this.usageData = null;
  }

  displayEnd(): number {
    const end = this.page * this.pageSize;
    return end > this.total ? this.total : end;
  }

  isExpired(coupon: CouponDto): boolean {
    const expiryDate = new Date(coupon.expiresAt);
    expiryDate.setHours(23, 59, 59, 999);
    return expiryDate < new Date();
  }

  getUsagePercent(coupon: CouponDto): number {
    if (!coupon.maxUses) return 0;
    return Math.round((coupon.currentUses / coupon.maxUses) * 100);
  }

  onCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.toUpperCase();
    this.form.get('code')!.setValue(input.value, { emitEvent: false });
  }
}
